// Copyright (c) 2026 Isolated Networks. All rights reserved.
// Proprietary and confidential. Unauthorized copying, distribution, or use
// of this file, via any medium, is strictly prohibited.
// SPDX-License-Identifier: LicenseRef-Proprietary

import React, {
  createContext,
  useState,
  useEffect,
  useContext,
  useCallback,
  useRef,
  useMemo,
} from 'react';
import axios from '../utils/axiosConfig';
import { isMaintenance } from '../utils/maintenanceMode';
import { isRemoteSessionActive } from '../utils/remoteSessionActivity';
import { getTokenExpiryMs, shouldRefreshToken } from '../utils/sessionRefresh';
import { getConfigValue, getPortalLoginUrl, getPortalLogoutUrl, getPortalSsoStartUrl } from '../config';
import { useNavigate } from 'react-router-dom';

const AuthContext = createContext();

let ssoBootstrapInflight = null;

export const useAuth = () => {
  return useContext(AuthContext);
};

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token') || null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  // Prevent multiple logout calls
  const isLoggingOutRef = useRef(false);
  // Reactive twin of isLoggingOutRef. ProtectedRoute/LoginRoute watch
  // currentUser to decide whether to auto-redirect to Portal SSO — the
  // moment logout() clears currentUser, React re-renders them in the SAME
  // batch as logout()'s own window.location.replace(...) to Portal's
  // /auth/logout, which has NOT actually navigated the browser away yet
  // (location.replace only schedules the navigation; synchronous JS and the
  // resulting re-render still run first). Without this flag, ProtectedRoute
  // sees "no user" and fires its OWN redirect to getPortalSsoStartUrl() —
  // Portal /login?next=… — which can win the race against the real logout
  // navigation (last location.replace() call wins) and silently signs the
  // user back in. See logout()/ProtectedRoute.
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  // Bumped on every logout. An in-flight token refresh captures the epoch
  // before its await; a mismatch after means logout intervened and the result
  // must be DISCARDED — otherwise the late /refresh response rewrites the
  // token and currentUser after logout ("session resurrection"), silently
  // re-arming every background poller while the app sits on /login.
  const logoutEpochRef = useRef(0);

  // Activity tracking for session renewal
  const lastActivityRef = useRef(Date.now());
  const lastRemoteRefreshRef = useRef(0);
  const refreshTimeoutRef = useRef(null);
  const activityEventsRef = useRef([
    'mousedown',
    'mousemove',
    'mouseup',
    'click',
    'dblclick',
    'keydown',
    'keypress',
    'keyup',
    'scroll',
    'wheel',
    'touchstart',
    'touchmove',
    'touchend',
    'focus',
    'blur',
    'input',
    'change',
    'contextmenu',
    'dragstart',
    'drop',
  ]);

  // Configuration for session management
  // Proactive refresh keys on TOKEN AGE (JWT lifetime 180 min): refresh once
  // less than this remains, as long as the user is still active. Generous on
  // purpose — refreshing early is free, refreshing late is a logout, and the
  // 60s check interval is throttled in background tabs.
  const REFRESH_WHEN_REMAINING = 30 * 60 * 1000;
  const ACTIVITY_TIMEOUT = 20 * 60 * 1000; // 20 min inactivity logout (backend ops continue independently)
  const CHECK_INTERVAL = 60 * 1000; // Check every 60 seconds for more responsive timeout

  // Logout function
  const logout = useCallback(() => {
    // Prevent multiple simultaneous logout calls
    if (isLoggingOutRef.current) {
      console.log('Logout already in progress, skipping...');
      return;
    }

    isLoggingOutRef.current = true;
    setIsLoggingOut(true);
    logoutEpochRef.current += 1;

    // Check if firmware upload is in progress
    let firmwareUploadsActive = false;
    const firmwareState = localStorage.getItem('firmwareUpdateState');
    if (firmwareState) {
      try {
        const state = JSON.parse(firmwareState);
        firmwareUploadsActive =
          state.updateInProgress || Object.keys(state.uploadProgress || {}).length > 0;
        if (firmwareUploadsActive) {
          console.log('🔄 Firmware upload in progress - uploads will continue in background');
          console.log('🔐 Redirecting to login page while preserving upload state');
        }
      } catch (e) {
        // If parsing fails, proceed with normal logout
      }
    }

    // Dispatch a logout event that components can listen to
    window.dispatchEvent(new Event('user-logout'));

    // Only clear non-firmware related state
    if (!firmwareUploadsActive) {
      // Clear all authentication state if no firmware uploads
      localStorage.removeItem('token');
      localStorage.removeItem('firmwareToken');
    } else {
      // Keep firmware token but clear main token to show logged out state
      localStorage.removeItem('token');
      // Keep firmwareToken for ongoing uploads
      console.log('📦 Preserving firmware token for background uploads');
    }

    setToken(null);
    setCurrentUser(null);
    setError(null); // Clear any existing errors

    // Clear any active timers
    if (refreshTimeoutRef.current) {
      clearTimeout(refreshTimeoutRef.current);
      refreshTimeoutRef.current = null;
    }

    const ssoEnabled = getConfigValue('sso.enabled', true);
    const portalLogout = getPortalLogoutUrl();
    const portalLogin = getPortalLoginUrl();

    if (ssoEnabled && portalLogout) {
      setTimeout(() => {
        isLoggingOutRef.current = false;
        setIsLoggingOut(false);
      }, 500);
      const next = encodeURIComponent(
        portalLogin ? `${portalLogin}${portalLogin.includes('?') ? '&' : '?'}logged_out=1` : '/login?logged_out=1'
      );
      window.location.replace(`${portalLogout}?next=${next}`);
      return;
    }

    const from = window.location.pathname + window.location.search + window.location.hash;
    if (from.startsWith('/login')) {
      navigate('/login');
    } else {
      navigate('/login', { state: { from } });
    }

    setTimeout(() => {
      isLoggingOutRef.current = false;
      setIsLoggingOut(false);
    }, 500);

    if (ssoEnabled && portalLogin && !portalLogout) {
      window.location.replace(
        `${portalLogin}${portalLogin.includes('?') ? '&' : '?'}logged_out=1`
      );
    }
  }, [navigate]);

  // Track session timeout state globally
  const sessionTimeoutStateRef = useRef({
    hasTimedOut: false,
    timeoutTime: null,
  });

  const redirectToPortalSso = () => {
    // logout() is already navigating the browser away (to Portal's own
    // /auth/logout) via its own window.location.replace(...). Firing a
    // SECOND, independent navigation here — to an auto-RELOGIN url — can
    // win that race (the last location.replace()/assign() call wins) and
    // silently sign the user back in instead of logging them out.
    if (isLoggingOutRef.current) return false;
    const returnTo = `${window.location.origin}/`;
    const target = getPortalSsoStartUrl(returnTo) || getPortalLoginUrl();
    if (!target || !getConfigValue('sso.enabled', true)) return false;

    const guardKey = 'sso_redirect_guard';
    const last = sessionStorage.getItem(guardKey);
    if (last && Date.now() - Number(last) < 8000) return false;
    sessionStorage.setItem(guardKey, String(Date.now()));
    window.location.assign(target);
    return true;
  };

  const bootstrap = useCallback(async () => {
    if (ssoBootstrapInflight) return ssoBootstrapInflight;

    const run = (async () => {
      try {
        setError(null);
        const response = await axios.get('/session/bootstrap');
        const { access_token, token_type, user } = response.data;
        localStorage.setItem('token', access_token);
        setToken(access_token);
        setCurrentUser(user);
        sessionTimeoutStateRef.current.hasTimedOut = false;
        sessionTimeoutStateRef.current.timeoutTime = null;
        lastActivityRef.current = Date.now();
        window.dispatchEvent(new Event('login'));
        return { access_token, token_type, user };
      } catch (error) {
        console.error('SSO bootstrap error:', error);
        setError(error.response?.data?.detail || 'SSO bootstrap failed');

        const existing = localStorage.getItem('token');
        if (existing) {
          setToken(existing);
          try {
            const me = await axios.get('/users/me');
            setCurrentUser(me.data);
            return { access_token: existing, token_type: 'bearer', user: me.data };
          } catch {
            localStorage.removeItem('token');
            setToken(null);
            setCurrentUser(null);
          }
        }

        redirectToPortalSso();
        throw error;
      } finally {
        ssoBootstrapInflight = null;
      }
    })();

    ssoBootstrapInflight = run;
    return run;
  }, []);

  useEffect(() => {
    const checkAuth = async () => {
      if (!token) {
        // logout() just cleared the token itself and is navigating the
        // browser away — a fresh bootstrap() here would both waste a round
        // trip and (via its own redirectToPortalSso() fallback) risk racing
        // that navigation. isLoggingOutRef also covers cross-tab/timeout
        // logouts that already cleared the token synchronously.
        if (getConfigValue('sso.enabled', true) && !isLoggingOutRef.current) {
          try {
            await bootstrap();
          } catch {}
        }
        setLoading(false);
        return;
      }

      try {
        const response = await axios.get('/users/me');
        setCurrentUser(response.data);
        setLoading(false);
      } catch (error) {
        console.error('Authentication error:', error);
        setLoading(false);
      }
    };
    checkAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, bootstrap]);

  useEffect(() => {
    const onStorage = (e) => {
      if (e.key !== 'token' || e.newValue) return;
      logoutEpochRef.current += 1;
      window.dispatchEvent(new Event('user-logout'));
      setToken(null);
      setCurrentUser(null);
      setError(null);
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
        refreshTimeoutRef.current = null;
      }
      const portalLogin = getPortalLoginUrl();
      if (getConfigValue('sso.enabled', true) && portalLogin) {
        window.location.assign(portalLogin);
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  // Token refresh function
  const refreshToken = useCallback(async () => {
    if (!token) return false;

    // Epoch guards the per-tab logout race; the localStorage check also covers
    // a logout in ANOTHER tab (storage is shared, epoch refs are not).
    const epochAtStart = logoutEpochRef.current;

    try {
      console.log('Refreshing token due to user activity...');
      const response = await axios.post('/refresh');
      const { access_token, user } = response.data;

      if (epochAtStart !== logoutEpochRef.current || !localStorage.getItem('token')) {
        console.log('Discarding token refresh that resolved after logout');
        return false;
      }

      localStorage.setItem('token', access_token);
      setToken(access_token);
      setCurrentUser(user);

      // Announce the rotation so listeners can react. Previously ONLY the axios
      // 401-recovery path (axiosConfig sharedTokenRefresh) fired this; the
      // proactive/remote-session refreshes here did not — so a long-open
      // http-view session (whose _hv_token auth cookie is pinned to the JWT at
      // open) never learned about the rotation and 401'd when that JWT expired.
      // The existing token-refreshed listener is idempotent (setToken to the same
      // value is a no-op), so emitting here is safe.
      window.dispatchEvent(
        new CustomEvent('token-refreshed', { detail: { token: access_token, user } })
      );

      console.log('Token refreshed successfully');
      return true;
    } catch (error) {
      // A refresh that FAILED after logout must not trigger a second logout
      // (re-dispatching user-logout noise while already on /login).
      if (epochAtStart !== logoutEpochRef.current) return false;
      console.error('Token refresh failed:', error);
      if (error.response && error.response.status === 401) {
        logout();
      }
      return false;
    }
  }, [token, logout]);

  // Activity tracking - update last activity time
  const updateActivity = useCallback(() => {
    lastActivityRef.current = Date.now();
  }, []);

  // Listen for session-timeout event dispatched by axios interceptor
  useEffect(() => {
    const handleSessionTimeout = () => {
      // Prevent handling multiple session timeout events — but only while the
      // token is actually gone. If a token EXISTS despite hasTimedOut (a
      // resurrected session), the logout must proceed or nothing ever ends it.
      if (
        (sessionTimeoutStateRef.current.hasTimedOut && !localStorage.getItem('token')) ||
        isLoggingOutRef.current
      ) {
        console.log('Session timeout already handled, skipping...');
        return;
      }

      // Mark that we've timed out and record the time
      sessionTimeoutStateRef.current.hasTimedOut = true;
      sessionTimeoutStateRef.current.timeoutTime = Date.now();

      console.log('Session timeout event received, logging out...');
      logout();
    };

    const handleTokenRefreshNeeded = async (event) => {
      // When 401 occurs but there are active operations, try to refresh token first
      console.log(
        '[AUTH] Token refresh needed due to 401 with active operations. Attempting refresh...',
        event.detail
      );
      try {
        const refreshSuccess = await refreshToken();
        if (refreshSuccess) {
          console.log('[AUTH] Token refreshed successfully, activity timer reset');
          updateActivity(); // Reset activity timer after successful refresh
        } else {
          console.warn('[AUTH] Token refresh failed, will logout on next 401');
        }
      } catch (err) {
        console.error('[AUTH] Error during token refresh:', err);
      }
    };

    // The axios interceptor's single-flight 401 recovery (sharedTokenRefresh
    // in axiosConfig) rotates the token in localStorage and announces it here.
    // Sync React state with the same discard rule as refreshToken(): if a
    // logout won the race (token gone from storage), do NOT resurrect.
    const handleTokenRefreshed = (event) => {
      const { token: newToken, user } = event.detail || {};
      if (!newToken || !localStorage.getItem('token') || isLoggingOutRef.current) return;
      setToken(newToken);
      if (user) setCurrentUser(user);
    };

    window.addEventListener('session-timeout', handleSessionTimeout);
    window.addEventListener('token-refresh-needed', handleTokenRefreshNeeded);
    window.addEventListener('token-refreshed', handleTokenRefreshed);

    return () => {
      window.removeEventListener('session-timeout', handleSessionTimeout);
      window.removeEventListener('token-refresh-needed', handleTokenRefreshNeeded);
      window.removeEventListener('token-refreshed', handleTokenRefreshed);
    };
  }, [logout, refreshToken, updateActivity]);

  // Set up activity listeners — only real user interactions (mouse, keyboard,
  // scroll, touch) count.  Background API polling must NOT reset the timer.
  useEffect(() => {
    if (!token) return;

    const handleActivity = () => {
      updateActivity();
    };

    activityEventsRef.current.forEach((event) => {
      document.addEventListener(event, handleActivity, true);
    });

    return () => {
      activityEventsRef.current.forEach((event) => {
        document.removeEventListener(event, handleActivity, true);
      });
    };
  }, [token, updateActivity]);

  // Periodic session management - check for token refresh needs
  useEffect(() => {
    if (!token) return;

    const checkSessionStatus = async () => {
      const now = Date.now();

      // Remote Web/RDP/SSH viewers swallow pointer/keyboard events inside iframes,
      // so the document never sees "activity" while the user is actively remoting.
      // Treat an open remote session as continuous activity and keep the JWT fresh.
      if (isRemoteSessionActive()) {
        lastActivityRef.current = now;
        // Refresh at most every 10 minutes (JWT lifetime is 180m).
        if (!isMaintenance() && now - lastRemoteRefreshRef.current > 10 * 60 * 1000) {
          lastRemoteRefreshRef.current = now;
          try {
            await refreshToken();
          } catch (e) {
            console.warn('[AUTH] Token refresh during remote session failed:', e);
          }
        }
        return;
      }

      const timeSinceActivity = now - lastActivityRef.current;

      // Inactivity timeout — backend operations (firmware push, pull, container
      // pulls) run server-side and continue independently of the UI session.
      if (timeSinceActivity >= ACTIVITY_TIMEOUT) {
        console.warn(
          `User inactive for ${Math.floor(timeSinceActivity / 1000)}s (limit: ${ACTIVITY_TIMEOUT / 1000}s). Logging out.`
        );
        sessionStorage.setItem('sessionTimedOut', '1');
        window.dispatchEvent(new Event('session-timeout'));
        return;
      }

      // User is still active, check if we need to refresh the token
      try {
        // Pause token/session checks while a restore is swapping the DBs (backend down).
        if (isMaintenance()) return;

        try {
          await axios.get('/users/me');
        } catch (tokenCheckError) {
          if (tokenCheckError.response && tokenCheckError.response.status === 401) {
            console.log('Token invalid, attempting refresh...');
            const refreshSuccess = await refreshToken();
            if (!refreshSuccess) {
              const veryRecentActivity = Date.now() - lastActivityRef.current < 30000;
              if (veryRecentActivity) {
                console.warn(
                  'Token refresh failed but user was recently active. Waiting before logout...'
                );
                updateActivity();
                return;
              }
              console.warn('Token refresh failed, logging out.');
              logout();
              return;
            }
          } else {
            throw tokenCheckError;
          }
        }

        // PROACTIVE refresh keyed to TOKEN AGE, not inactivity. The previous
        // condition (`timeSinceActivity > ACTIVITY_TIMEOUT - threshold`) only
        // refreshed users who had been IDLE 15-20 minutes — a continuously
        // ACTIVE user never refreshed, aged out the 180-min JWT mid-click and
        // was hard-logged-out (2026-07-27 incident). Read the token from
        // localStorage, not React state: an interceptor-driven refresh
        // (axiosConfig) may have rotated it since the last render.
        const expMs = getTokenExpiryMs(localStorage.getItem('token'));
        if (
          shouldRefreshToken({ expMs, nowMs: now, refreshWindowMs: REFRESH_WHEN_REMAINING })
        ) {
          console.log('Access token expires soon — refreshing proactively...');
          await refreshToken();
        }
      } catch (error) {
        if (error.response && error.response.status === 401) {
          console.warn('Session expired during periodic check. Logging out.');
          logout();
        } else {
          console.error('Session check error:', error);
        }
      }
    };

    const interval = setInterval(checkSessionStatus, CHECK_INTERVAL);

    // Run check immediately on mount
    checkSessionStatus();

    // Background tabs get their timers throttled and laptops sleep through
    // the 60s cadence entirely — the refresh window can be missed. Re-check
    // the moment the tab becomes visible again; together with the backend's
    // refresh grace window this heals any gap short enough to also pass the
    // inactivity check above.
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        checkSessionStatus();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [token, logout, refreshToken, updateActivity]);

  // Pre-logout inactivity WARNING (added 2026-06-16 per user request — a net-new feature beyond
  // the original Python+React app, which only showed a banner AFTER logout). A 1-second timer
  // watches the same inactivity clock as checkSessionStatus and, during the final WARNING_LEAD
  // window, surfaces SessionWarningModal with a live countdown + a "Stay signed in" action.
  //
  // IMPORTANT: once the warning fires we LATCH an absolute logout deadline and stop recomputing
  // from lastActivityRef. The modal itself (its backdrop, autoFocus, and any mouse movement while
  // reading it) generates 'focus'/'mousemove' events that the activity listeners would otherwise
  // treat as "user is active", perpetually pushing the deadline out — which is exactly why the old
  // version appeared to hang at ~60s and never logged the user out. With the deadline latched, the
  // countdown is deterministic: the ONLY thing that cancels it is the explicit 'session-stay'
  // signal from the "Stay signed in" button. When the latched deadline passes we fire the same
  // 'session-timeout' event the periodic check uses, so logout actually happens.
  const WARNING_LEAD = 60 * 1000; // warn 60s before the inactivity logout
  useEffect(() => {
    if (!token) return;
    let deadline = null; // absolute ms timestamp at which we log out (latched once warned)
    let firedTimeout = false;

    const fireTimeout = () => {
      if (firedTimeout) return;
      firedTimeout = true;
      sessionStorage.setItem('sessionTimedOut', '1');
      window.dispatchEvent(new Event('session-timeout'));
    };

    // Explicit "Stay signed in" — the only thing that cancels an active warning.
    const onStay = () => {
      deadline = null;
      window.dispatchEvent(new Event('session-inactivity-clear'));
    };
    window.addEventListener('session-stay', onStay);

    const tick = () => {
      const now = Date.now();
      // Open remote sessions count as activity — cancel any pending warning.
      if (isRemoteSessionActive()) {
        lastActivityRef.current = now;
        if (deadline != null) {
          deadline = null;
          window.dispatchEvent(new Event('session-inactivity-clear'));
        }
        return;
      }
      if (deadline == null) {
        // Not yet warning — track the live inactivity clock.
        const projectedLogout = lastActivityRef.current + ACTIVITY_TIMEOUT;
        const remaining = projectedLogout - now;
        if (remaining <= 0) {
          fireTimeout();
        } else if (remaining <= WARNING_LEAD) {
          deadline = projectedLogout; // LATCH the deadline; ignore activity noise from here on
          window.dispatchEvent(
            new CustomEvent('session-inactivity-warning', {
              detail: { deadline },
            })
          );
        }
      } else if (now >= deadline) {
        // Latched deadline reached — log out for real.
        window.dispatchEvent(new Event('session-inactivity-clear'));
        fireTimeout();
        deadline = null;
      }
    };
    const id = setInterval(tick, 1000);
    return () => {
      clearInterval(id);
      window.removeEventListener('session-stay', onStay);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Login function (real backend) - Send JSON data
  const login = async (username, password) => {
    try {
      setError(null);

      // Call backend for login with JSON data
      const response = await axios.post(
        '/login',
        {
          username,
          password,
        },
        {
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      const { access_token, token_type, user } = response.data;
      localStorage.setItem('token', access_token);
      setToken(access_token);
      setCurrentUser(user);

      // Reset session timeout state on successful login
      sessionTimeoutStateRef.current.hasTimedOut = false;
      sessionTimeoutStateRef.current.timeoutTime = null;

      // Update activity timestamp to prevent immediate timeout
      lastActivityRef.current = Date.now();

      // Dispatch login event for components to listen to (e.g., firmware upload resumption)
      window.dispatchEvent(new Event('login'));

      return { access_token, token_type, user };
    } catch (error) {
      console.error('Login error:', error);
      setError(error.response?.data?.detail || 'Login failed. Please check your credentials.');
      throw error;
    }
  };

  const isAdmin = useCallback(() => {
    return currentUser?.role === 'admin';
  }, [currentUser]);

  const isAuthenticated = !!token && !!currentUser;

  const value = useMemo(
    () => ({
      currentUser,
      loading,
      error,
      login,
      bootstrap,
      logout,
      isAdmin,
      refreshToken,
      updateActivity,
      isAuthenticated,
      isLoggingOut,
    }),
    [
      currentUser,
      loading,
      error,
      login,
      bootstrap,
      logout,
      isAdmin,
      refreshToken,
      updateActivity,
      isAuthenticated,
      isLoggingOut,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
